import { useCallback, useContext, useEffect, useState } from 'react'

import { debounce } from 'lodash'
import { useSelector } from 'react-redux'

import { GlobalContext } from '../../../Providers/GlobalProvider'
import { GlobalFiltersContext } from '../../../Providers/GlobalFiltersProvider'
import { Metadata, retrieveMetadata } from '../../../utils/http'
import { TableParameters } from '../../../utils/entities/tableParameters'
import { MACCS_ENVIRONMENT, OrderBy } from '../../../utils/constants'
import { deleteUser, getPaginateUsers } from '../../../utils/api/user'
import { onError, onSuccess } from '../../../utils/apiHelper'
import UserFormModal from '../../../HOC/Forms/UserModal'
import UsersTable from '../../shared/UsersView/UsersTable'
import AdminResetPassword from '../../../Components/shared/AdminResetPassword'
import { TableActions } from '../../../Components/antd/Tables/DataTable/utils/actions'
import UserSpecialities from './UserSpecialities'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

const UsersView = () => {
  const { user } = useContext(GlobalContext)
  const { selectedInstitution } = useContext(GlobalFiltersContext)

  const t = useSelector(selectTranslate)

  const [users, setUsers] = useState({ data: [], meta: new Metadata({ pageSize: 20 }) })
  const [tableParameters, setTableParameters] = useState(new TableParameters({ orderBy: OrderBy.EMAIL, pageSize: 20, filters: { institution: selectedInstitution } }))
  const [selectedUserPassword, setSelectedUserPassword] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchUsers = (user, filters) => {
    setLoading(true)

    getPaginateUsers(user, { ...filters, environment: MACCS_ENVIRONMENT }).then(json => {
      if (json?.data || json?.meta) {
        const users = {}

        users.data = json?.data ?? []
        users.meta = json?.meta ? retrieveMetadata(json.meta) : new Metadata()

        setUsers(users)
      }

      setLoading(false)
    })
  }

  const handleDeleteUser = useCallback(userToDelete => {
    deleteUser(user, userToDelete).then(json => {
      if (json?.data) {
        onSuccess(t('user_view.actions.delete-user.success'))
        fetchUsers(user, tableParameters)
      } else {
        onError(t('user_view.actions.delete-user.error'))
      }
    })
  }, [user, tableParameters, t])

  useEffect(() => {
    const fetch = debounce(() => fetchUsers(user, tableParameters), 300)

    fetch()

    return () => {
      fetch.cancel()
    }
  }, [user.institutionGroup, tableParameters])

  const handleTriggerAction = useCallback((user, action) => {
    if (action === TableActions.DELETE) {
      handleDeleteUser(user)
    } else if (action === TableActions.RESET_USER_PASSWORD) {
      setSelectedUserPassword(user)
    } else if (action === TableActions.DISPLAY_USER_SPECIALITIES) {
      setSelectedUser(user)
    }
  }, [handleDeleteUser])

  return (
    <div className='users-view-container'>
      <UserFormModal
        onSave={() => fetchUsers(user, tableParameters)}
        onTriggerAction={handleTriggerAction}
      >
        <UsersTable
          parameters={tableParameters}
          loading={loading}
          users={users}
          onParametersChange={setTableParameters}
          onSelectUserSectors={setSelectedUser}
        />
      </UserFormModal>
      <UserSpecialities selectedUser={selectedUser} onClose={() => setSelectedUser(null)} />
      <AdminResetPassword
        userToResetPassword={selectedUserPassword}
        onClose={() => setSelectedUserPassword(null)}
      />
    </div>
  )
}

export default UsersView
